import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { LogOut, User2, ShieldCheck, HeartHandshake, FileSpreadsheet, Users, GitMerge, IdCard, Bell, Trash2, ChevronDown } from "lucide-react";
import { useI18n } from "../lib/i18n";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const FLAG_CODE = { en: "gb", fr: "fr", de: "de", es: "es", it: "it" };

function Flag({ lang, className = "" }) {
  const code = FLAG_CODE[lang] || "gb";
  return (
    <img
      src={`https://flagcdn.com/24x18/${code}.png`}
      srcSet={`https://flagcdn.com/48x36/${code}.png 2x`}
      width={20}
      height={15}
      alt=""
      className={`inline-block rounded-[2px] object-cover ${className}`}
    />
  );
}

export default function NavBar() {
  const { user, logout } = useAuth();
  const { t, lang, setLang, langs } = useI18n();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const isAdmin = user && user.role === "admin";
  const isActive = user && (user.role === "family" || user.role === "admin" || user.status === "active");

  const [highMatches, setHighMatches] = useState(0);
  const [notifCount, setNotifCount] = useState(0);
  const [myNotif, setMyNotif] = useState(0);
  useEffect(() => {
    if (user && user.role) {
      api.get("/notifications").then((r) => setMyNotif(r.data.unread || 0)).catch(() => {});
    }
    if (!isAdmin) return;
    api.get("/admin/high-matches/count")
      .then((r) => setHighMatches(r.data.count || 0))
      .catch(() => {});
    api.get("/admin/notifications", { params: { unread_only: true } })
      .then((r) => setNotifCount(r.data.unread || 0))
      .catch(() => {});
  }, [isAdmin]);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-30 w-full border-b border-[#E2E8F0] bg-white/85 backdrop-blur-md"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-2 font-heading text-lg font-semibold text-[#1A2024]"
        >
          <span className="grid h-9 w-9 place-items-center overflow-hidden rounded-full bg-white ring-1 ring-[#E2E8F0]">
            <img src="/eurofind-logo.png" alt="EuroFind" className="h-full w-full object-cover" />
          </span>
          EuroFind
        </Link>

        <nav className="hidden items-center gap-6 md:flex font-body text-sm text-[#5C6873]">
          <Link to="/" className="hover:text-[#1A2024] transition-base" data-testid="nav-home">{t("nav.home")}</Link>
          {user && user.role && (
            <Link to="/dashboard" className="hover:text-[#1A2024] transition-base" data-testid="nav-dashboard">{t("nav.dashboard")}</Link>
          )}
          {user && isActive && (
            <Link to="/matches" className="hover:text-[#1A2024] transition-base" data-testid="nav-matches">{t("nav.matches")}</Link>
          )}
          {(!user || user.role !== "family") && (
            <Link to="/browse" className="hover:text-[#1A2024] transition-base" data-testid="nav-browse">{t("nav.browse")}</Link>
          )}
          {user && user.role && (
            <Link to="/notifications" className="relative inline-flex items-center gap-1 hover:text-[#1A2024] transition-base" data-testid="nav-my-notifications">
              <Bell size={14} />
              {myNotif > 0 && (
                <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#9E4747] px-1 font-body text-[10px] font-semibold text-white" data-testid="nav-my-notif-badge">
                  {myNotif}
                </span>
              )}
            </Link>
          )}
          {isAdmin && (
            <Link to="/admin/alerts" className="relative inline-flex items-center gap-1 hover:text-[#1A2024] transition-base" data-testid="nav-alerts">
              <Bell size={14} /> Alerts
              {highMatches > 0 && (
                <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#9E4747] px-1 font-body text-[10px] font-semibold text-white" data-testid="nav-alerts-badge">
                  {highMatches}
                </span>
              )}
              {notifCount > 0 && (
                <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#C99B4B] px-1 font-body text-[10px] font-semibold text-white" data-testid="nav-notif-badge">
                  {notifCount}
                </span>
              )}
            </Link>
          )}
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="inline-flex items-center gap-1 font-body text-sm text-[#5C6873] hover:text-[#1A2024] transition-base" data-testid="nav-admin-menu">
                  <ShieldCheck size={14} /> Admin <ChevronDown size={13} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem asChild><Link to="/admin/authorities" className="flex w-full items-center gap-2" data-testid="nav-admin"><ShieldCheck size={14} /> {t("nav.admin")}</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/admin/users" className="flex w-full items-center gap-2" data-testid="nav-users"><Users size={14} /> Users</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/admin/accounts" className="flex w-full items-center gap-2" data-testid="nav-accounts"><IdCard size={14} /> Accounts</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/admin/person-files" className="flex w-full items-center gap-2" data-testid="nav-person-files"><GitMerge size={14} /> Person Files</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/admin/liaisons" className="flex w-full items-center gap-2" data-testid="nav-liaisons"><HeartHandshake size={14} /> Liaisons</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/admin/deletion-logs" className="flex w-full items-center gap-2" data-testid="nav-deletion-logs"><Trash2 size={14} /> Deletions</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/admin/import" className="flex w-full items-center gap-2" data-testid="nav-import"><FileSpreadsheet size={14} /> Import</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/admin/helpers" className="flex w-full items-center gap-2" data-testid="nav-helpers"><HeartHandshake size={14} /> Helpers</Link></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-[#2E3A46]" data-testid="lang-switcher">
                <Flag lang={lang} className="mr-1.5" /> {lang.toUpperCase()}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {langs.map((l) => (
                <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} data-testid={`lang-option-${l.code}`} className="gap-2">
                  <Flag lang={l.code} /> {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {user && user.role ? (
            <>
              <span className="hidden items-center gap-2 text-sm text-[#5C6873] md:flex" data-testid="navbar-user">
                <User2 size={16} /> {user.name}
                <span className="rounded-full border border-[#E2E8F0] bg-[#F8F9FA] px-2 py-0.5 text-xs uppercase tracking-wide">
                  {user.role}
                </span>
                {user.role === "authority" && user.status !== "active" && (
                  <span className="rounded-full border border-[#C99B4B]/40 bg-[#C99B4B]/15 px-2 py-0.5 text-[10px] uppercase tracking-wide text-[#8A6A2E]" data-testid="navbar-pending-badge">
                    {user.status}
                  </span>
                )}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={onLogout}
                data-testid="navbar-logout-btn"
                className="border-[#E2E8F0] text-[#2E3A46]"
              >
                <LogOut size={14} className="mr-1" /> {t("nav.signout")}
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="navbar-login-link">
                <Button variant="ghost" size="sm" className="text-[#2E3A46]">{t("nav.signin")}</Button>
              </Link>
              <Link to="/register" data-testid="navbar-register-link">
                <Button size="sm" className="bg-[#2E3A46] hover:bg-[#1A2024] text-white">{t("nav.create_account")}</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
